/**
 * Parent Portal - Live Bus Tracking Page
 * Shows bus route, current position, ETA, driver info
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalBusTrackingPage({ token, user }) {
  const { language } = useLanguage();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const studentId = user?.student_profile_id || user?.id;

  const loadTracking = useCallback(async () => {
    if (!studentId) { setLoading(false); setError('no_student'); return; }
    try {
      const res = await fetch(`${API_URL}/api/transport/parent-tracking/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTracking(data.tracking);
        setError(null);
      } else {
        setError(data.message || 'not_assigned');
      }
    } catch {
      setError('network');
    }
    setLoading(false);
    setLastRefresh(new Date());
  }, [studentId, token]);

  useEffect(() => { loadTracking(); }, [loadTracking]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadTracking, 30000);
    return () => clearInterval(interval);
  }, [loadTracking]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="bus-tracking-loading">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4" data-testid="bus-tracking-error">
        <Header language={language} />
        <div className="text-center py-16 bg-white rounded-xl border text-gray-400">
          <div className="text-5xl mb-3">🚌</div>
          <p className="text-lg font-medium">
            {error === 'not_assigned' || error === 'No transport assigned'
              ? (language === 'hi' ? 'ट्रांसपोर्ट assign नहीं है' : 'No transport assigned')
              : error === 'no_student'
              ? (language === 'hi' ? 'छात्र प्रोफ़ाइल नहीं मिली' : 'Student profile not found')
              : (language === 'hi' ? 'डेटा लोड नहीं हो सका' : 'Could not load data')}
          </p>
          <p className="text-sm mt-2">
            {language === 'hi' ? 'कृपया स्कूल से संपर्क करें' : 'Please contact the school'}
          </p>
        </div>
      </div>
    );
  }

  if (!tracking) return null;

  const t = tracking;
  const statusColors = {
    on_route_morning: 'bg-green-500',
    on_route_afternoon: 'bg-blue-500',
    at_school: 'bg-orange-500',
    not_running: 'bg-gray-400',
  };

  return (
    <div className="space-y-4" data-testid="bus-tracking-page">
      <Header language={language} />

      {/* Live Status Banner */}
      <div className={`rounded-xl p-4 text-white ${statusColors[t.status] || 'bg-gray-500'}`} data-testid="bus-status-banner">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {t.status.startsWith('on_route') && (
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
                </span>
              )}
              <span className="font-bold text-lg">
                {language === 'hi' ? t.status_text_hi : t.status_text_en}
              </span>
            </div>
            {t.eta_minutes > 0 && (
              <p className="text-sm opacity-90">
                {language === 'hi'
                  ? `आपके स्टॉप तक ~${t.eta_minutes} मिनट`
                  : `~${t.eta_minutes} min to your stop`}
              </p>
            )}
            {t.eta_minutes === 0 && t.status.startsWith('on_route') && (
              <p className="text-sm font-medium animate-pulse">
                {language === 'hi' ? 'बस आपके स्टॉप पर पहुँच गई!' : 'Bus has reached your stop!'}
              </p>
            )}
          </div>
          <div className="text-right text-sm opacity-80">
            <div>{t.route_name}</div>
            {t.vehicle && <div className="font-mono font-bold">{t.vehicle.number}</div>}
          </div>
        </div>
      </div>

      {/* Bus Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <InfoCard
          icon="🚌"
          label={language === 'hi' ? 'वाहन' : 'Vehicle'}
          value={t.vehicle?.number || '-'}
          sub={t.vehicle?.type || ''}
        />
        <InfoCard
          icon="🗺️"
          label={language === 'hi' ? 'रूट' : 'Route'}
          value={t.route_name}
          sub={`${t.start_point} → ${t.end_point}`}
        />
        <InfoCard
          icon="🕐"
          label={language === 'hi' ? 'पिकअप' : 'Pickup'}
          value={t.pickup_time}
          sub={t.student_stop || '-'}
        />
        <InfoCard
          icon="🕑"
          label={language === 'hi' ? 'ड्रॉप' : 'Drop'}
          value={t.drop_time}
          sub={t.student_stop || '-'}
        />
      </div>

      {/* Route Visual - Metro Line Style */}
      <div className="bg-white rounded-xl border p-5" data-testid="route-visual">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">
            {language === 'hi' ? 'रूट का नक्शा' : 'Route Map'}
          </h3>
          <button data-testid="refresh-btn" onClick={loadTracking}
            className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200 transition flex items-center gap-1">
            🔄 {language === 'hi' ? 'रिफ्रेश' : 'Refresh'}
          </button>
        </div>

        {/* Stops visualization */}
        <div className="relative pl-8">
          {t.stops.map((stop, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === t.stops.length - 1;

            return (
              <div key={idx} className="relative" data-testid={`stop-${idx}`}>
                {/* Vertical line */}
                {!isLast && (
                  <div className={`absolute left-0 top-5 w-0.5 h-full -ml-4 ${stop.is_passed ? 'bg-green-400' : 'bg-gray-200'}`} />
                )}

                {/* Stop node */}
                <div className="flex items-start gap-3 pb-6">
                  <div className={`absolute -left-6 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10 ${
                    stop.is_current
                      ? 'bg-green-500 border-green-600 ring-4 ring-green-200 animate-pulse'
                      : stop.is_student_stop
                      ? 'bg-orange-500 border-orange-600 ring-2 ring-orange-200'
                      : stop.is_passed
                      ? 'bg-green-400 border-green-500'
                      : 'bg-white border-gray-300'
                  }`}>
                    {stop.is_current && <span className="text-white text-[8px]">🚌</span>}
                    {stop.is_student_stop && !stop.is_current && <span className="text-white text-[8px]">📍</span>}
                  </div>

                  <div className="ml-2 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        stop.is_current ? 'text-green-700' :
                        stop.is_student_stop ? 'text-orange-700 font-bold' :
                        stop.is_passed ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {stop.name}
                      </span>
                      {stop.is_student_stop && (
                        <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded text-[10px] font-bold">
                          {language === 'hi' ? 'आपका स्टॉप' : 'Your Stop'}
                        </span>
                      )}
                      {stop.is_current && (
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-600 rounded text-[10px] font-bold animate-pulse">
                          {language === 'hi' ? 'बस यहाँ है' : 'Bus Here'}
                        </span>
                      )}
                      {isFirst && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-[10px] font-medium">Start</span>
                      )}
                      {isLast && (
                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-[10px] font-medium">
                          {language === 'hi' ? 'स्कूल' : 'School'}
                        </span>
                      )}
                    </div>
                    {stop.time && <span className="text-xs text-gray-400">{stop.time}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {lastRefresh && (
          <div className="text-xs text-gray-400 mt-2 text-right">
            {language === 'hi' ? 'अंतिम अपडेट' : 'Last updated'}: {lastRefresh.toLocaleTimeString('en-IN')}
            <span className="ml-2 opacity-60">{language === 'hi' ? '(हर 30 सेकंड ऑटो रिफ्रेश)' : '(auto-refresh every 30s)'}</span>
          </div>
        )}
      </div>

      {/* Driver Card */}
      {t.driver && t.driver.name && (
        <div className="bg-white rounded-xl border p-4" data-testid="driver-card">
          <h3 className="font-semibold text-gray-800 mb-3">
            {language === 'hi' ? 'ड्राइवर की जानकारी' : 'Driver Information'}
          </h3>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center text-2xl">
              👨‍✈️
            </div>
            <div className="flex-1">
              <div className="font-bold text-gray-800 text-lg">{t.driver.name}</div>
              <div className="text-sm text-gray-500">{t.vehicle?.number} ({t.vehicle?.type})</div>
            </div>
            {t.driver.phone && (
              <a href={`tel:${t.driver.phone}`} data-testid="call-driver-btn"
                className="flex items-center gap-2 px-5 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition shadow-md">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {language === 'hi' ? 'कॉल करें' : 'Call'}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Safety Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700" data-testid="safety-notice">
        <div className="font-semibold mb-1">{language === 'hi' ? 'सुरक्षा सूचना' : 'Safety Notice'}</div>
        <p className="text-xs opacity-80">
          {language === 'hi'
            ? 'यह ट्रैकिंग GPS device कनेक्ट होने पर real-time होगी। अभी यह estimated schedule के आधार पर है। किसी emergency में तुरंत ड्राइवर को कॉल करें।'
            : 'This tracking will be real-time once GPS device is connected. Currently based on estimated schedule. In case of emergency, call the driver immediately.'}
        </p>
      </div>
    </div>
  );
}

function Header({ language }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          {language === 'hi' ? 'बस ट्रैकिंग' : 'Bus Tracking'}
        </h1>
        <p className="text-sm text-gray-500">
          {language === 'hi' ? 'अपने बच्चे की बस लाइव ट्रैक करें' : 'Track your child\'s bus live'}
        </p>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border p-3">
      <div className="text-lg mb-1">{icon}</div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-bold text-gray-800 truncate">{value}</div>
      {sub && <div className="text-[10px] text-gray-400 truncate">{sub}</div>}
    </div>
  );
}
